import { PaginatedResponse, useApi } from '@/api';
import { Link } from '@/components';
import {
  CurrencyValue,
  DataTable,
  Flex,
  NetworkIcon,
  TableSkeleton,
  useIsMobile,
  useTableProps
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';
import _ from 'lodash';

type ItemData = {
  usds_balance: string;
  susds_balance: string;
  usdc_balance: string;
  total_assets: string;
  usds_price: string;
  susds_price: string;
  usdc_price: string;
  network: string;
};

export default function PSM3Table({
  network = null
}: {
  network?: string | null;
}) {
  const isMobile = useIsMobile();
  const { pagination, sorting } = useTableProps({
    pageSize: 10,
    initialSorting: '-total_assets',
    filtersDebounceTime: 500,
    initialFilters: {
      selectedOption: ''
    }
  });
  const [data, error] = useApi<PaginatedResponse<ItemData[]>>({
    url: `/sparkstar/sll/psm3s/`,
    queryParams: {
      p: pagination.currentPage,
      p_size: pagination.pageSize,
      order: sorting.currentSorting,
      network
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  const columns: TableColumn[] = [
    {
      header: 'Network',
      getCell: (row) => (
        <Flex gap="xs" alignItems="center">
          <NetworkIcon network={row.network} size="2" />
          {_.capitalize(row.network)}
        </Flex>
      ),
      columnSize: isMobile ? '100px' : '150px',
      sticky: true
    },
    {
      header: 'USDC',
      getCell: (row) => (
        <CurrencyValue
          value={row.usdc_balance}
          options={{
            currency: 'USD',
            absolute: true
          }}
        />
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'usdc_balance'
    },
    {
      header: 'USDS',
      getCell: (row) => (
        <CurrencyValue
          value={row.usds_balance_usd}
          options={{
            currency: 'USD',
            absolute: true
          }}
        />
      ),

      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'usds_balance'
    },
    {
      header: 'sUSDS',
      getCell: (row) => (
        <CurrencyValue
          value={row.susds_balance_usd}
          options={{
            currency: 'USD',
            absolute: true
          }}
        />
      ),

      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'susds_balance'
    },

    {
      header: 'Total Assets',
      getCell: (row) => (
        <CurrencyValue
          value={row.total_assets}
          options={{
            currency: 'USD',
            absolute: true
          }}
        />
      ),
      cellAlign: 'flex-end',
      headerAlign: 'flex-end',
      sortingField: 'total_assets'
    }
  ];
  return (
    <DataTable
      rows={data.results}
      columns={columns}
      getKey={(row) => row.network}
      sorting={sorting}
      rowLinkProps={{
        to: (row) => `/spark-liquidity-layer/${row.network}/psm3`,
        // @ts-ignore
        linkComponent: Link
      }}
      pagination={{
        ...pagination,
        totalRecords: data.count
      }}
    />
  );
}
